import { Css, Icon, IconButton, IconKey, Palette, RightPanePanel, useTestIds } from "@homebound/beam";
import { ReactNode, useState } from "react";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { usePromoteToCheckout } from "src/routes/developments/product-offerings/components/usePromoteToCheckout";
import { usePublishProductOffering } from "src/routes/developments/product-offerings/components/usePublishProductOffering";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import {
  hasPendingPlanPackageUpdates,
  PackageVersionStatus,
} from "src/routes/libraries/product-offerings/overview/PackageVersionStatus";
import {
  ProductOfferingOverviewPanel_ProductOfferingFragment,
  useProductOfferingOverviewPanelQuery,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingOverviewPanel.generated";
import { productOfferingStatusTag } from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTable";
import { createDesignPackageUrl, createPlanPackageUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { programDataBathRange, programDataRange } from "src/utils/programData";

type ProductOfferingOverviewPanelProps = { productOfferingId: string; versionId: string };

/** Right-pane summary of one product offering, with promote(/restore) & publish actions. */
export function ProductOfferingOverviewPanel(props: ProductOfferingOverviewPanelProps) {
  const { productOfferingId, versionId } = props;
  // Pinned to the row's latest version so `aggregateDraft.canPublish` is computed against the draft, like the table's
  // `versionMode: LATEST` query; unpinned, the BE computes it against the active version and that result overwrites
  // the table's in the Apollo cache
  const query = useProductOfferingOverviewPanelQuery({ variables: { id: productOfferingId, versionId } });
  // Keyed by the latest version so a refetch that changes it resets the selected version
  return queryResult(query, ({ productOffering }) => (
    <ProductOfferingOverviewPanelView key={productOffering.aggregateLatest.id} productOffering={productOffering} />
  ));
}

type ProductOfferingOverviewPanelViewProps = { productOffering: ProductOfferingOverviewPanel_ProductOfferingFragment };

function ProductOfferingOverviewPanelView(props: ProductOfferingOverviewPanelViewProps) {
  const { productOffering: po } = props;
  const tid = useTestIds({}, "productOfferingOverviewPanel");
  const publish = usePublishProductOffering(po);
  const latestId = po.aggregateLatest.id;
  const [selectedVersionId, setSelectedVersionId] = useState(latestId);
  const promoteToCheckout = usePromoteToCheckout(po.id, selectedVersionId);

  const { minProgramData: min, maxProgramData: max } = po;
  const stats: [IconKey, ReactNode, string][] = [
    ["sqFeet", programDataRange(min, max, "sellableSqft") ?? "—", "Square ft"],
    ["bed", programDataRange(min, max, "bedrooms") ?? "—", "Beds"],
    ["bath", programDataBathRange(min, max) ?? "—", "Baths"],
  ];
  const packages = [
    {
      label: "Plan Package",
      tid: tid.openPlanPackage,
      name: po.planPackage.name,
      version: po.planPackageVersion.displayVersion,
      // PP publishes are synced into the POF automatically, so the PP dot is always green.
      hasUpdates: false,
      href: createPlanPackageUrl(po.planPackage.id, po.planPackageVersion.id),
    },
    {
      label: "Interior",
      tid: tid.openInterior,
      name: po.interiorDesignPackage.name,
      version: po.interiorDesignPackageVersion.displayVersion,
      hasUpdates: hasPendingPlanPackageUpdates(po.interiorDesignPackage, po.planPackage.id),
      href: createDesignPackageUrl(po.interiorDesignPackage.id, po.interiorDesignPackageVersion.id),
    },
    {
      label: "Exterior",
      tid: tid.openExterior,
      name: po.exteriorDesignPackage.name,
      version: po.exteriorDesignPackageVersion.displayVersion,
      hasUpdates: hasPendingPlanPackageUpdates(po.exteriorDesignPackage, po.planPackage.id),
      href: createDesignPackageUrl(po.exteriorDesignPackage.id, po.exteriorDesignPackageVersion.id),
    },
  ];

  return (
    <RightPanePanel
      title={po.name}
      tag={{ kind: "status", ...productOfferingStatusTag[po.effectiveStatus.code] }}
      secondaryAction={{ label: "Publish", disabled: publish.disabled, onClick: publish.onClick }}
      primaryAction={{
        label: promoteToCheckout.isRestore ? "Restore" : "Promote",
        icon: "cart",
        disabled: promoteToCheckout.disabled,
        onClick: promoteToCheckout.onClick,
      }}
    >
      <div css={Css.df.fdc.gap3.$} {...tid}>
        <img src={po.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG} alt="" css={Css.w100.hPx(176).br8.objectCover.$} />
        <div css={Css.df.$} {...tid.stats}>
          {stats.map(([icon, value, label], i) => (
            <div key={label} css={Css.df.fdc.gapPx(2).px2.if(i === 0).pl0.else.bl.bcGray200.$}>
              <div css={Css.df.aic.gap1.sm.$}>
                <Icon icon={icon} color={Palette.Gray700} inc={2.5} />
                {value}
              </div>
              <div css={Css.sm.gray700.$}>{label}</div>
            </div>
          ))}
        </div>
        <div>
          <div css={Css.mdSb.mb1.$}>Offering status</div>
          <div css={Css.bt.bcGray200.$} {...tid.offeringStatus}>
            <OfferingStatusRow label="Product Offering" name={po.name}>
              <ReadyPlanVersionHeader
                readyPlan={po}
                versionId={selectedVersionId}
                aggregateCheckoutId={po.aggregateCheckout?.id}
                onSelect={(versionId) => versionId && setSelectedVersionId(versionId)}
              />
            </OfferingStatusRow>
            {packages.map((p) => (
              <OfferingStatusRow key={p.label} label={p.label} name={p.name}>
                <PackageVersionStatus version={p.version} hasUpdates={p.hasUpdates} />
                <IconButton
                  icon="linkExternal"
                  label={`Open ${p.label.toLowerCase()}`}
                  onClick={p.href}
                  openInNew
                  color={Palette.Blue700}
                  compact
                  {...p.tid}
                />
              </OfferingStatusRow>
            ))}
          </div>
          {/* TODO: Add Recent Changes: https://linear.app/homebound-team/issue/PLAN-2012/fe-implement-the-recent-changes-section-in-the-overview-side-panel */}
        </div>
      </div>
    </RightPanePanel>
  );
}

type OfferingStatusRowProps = { label: string; name: string; children: ReactNode };

function OfferingStatusRow(props: OfferingStatusRowProps) {
  const { label, name, children } = props;
  return (
    <div css={Css.df.aic.gap1.py1.bb.bcGray200.xs.$}>
      <div css={Css.fg1.mw0.$}>
        <div css={Css.gray700.$}>{label}</div>
        <div css={Css.truncate.$}>{name}</div>
      </div>
      {children}
    </div>
  );
}
